'use client'

import { SelectOption, Tag } from '@/types'
import { FocusEventHandler, useEffect, useState } from 'react'
import { RefCallBack } from 'react-hook-form'
import { IoChevronDown } from 'react-icons/io5'
import { MdOutlineClear } from 'react-icons/md'

import Select, {
  components,
  ControlProps,
  MultiValueRemoveProps,
  PlaceholderProps,
  IndicatorSeparatorProps,
  DropdownIndicatorProps,
  ClearIndicatorProps,
  PropsValue,
} from 'react-select'

interface MultiSelectProps {
  closeMenuOnSelect?: boolean
  placeholder?: string
  options: Array<{ value: string; label: string }>
  isDisableSelect?: boolean
  onSelectValue: (value: Array<SelectOption>) => void
  onFocus?: FocusEventHandler<HTMLInputElement>
  onBlur?: FocusEventHandler<HTMLInputElement>
  defaultValue?: PropsValue<SelectOption>
  ref?: RefCallBack
  color?: 'grayborder' | 'failure'
  menuPlacement?: 'top' | 'bottom' | 'auto'
}

const Placeholder = (props: PlaceholderProps) => {
  return <components.Placeholder className="text-sm text-gray-500" {...props} />
}

const ControlComponent = (props: ControlProps) => (
  <components.Control className="!rounded-lg p-0.5" {...props} />
)

const MultiValueRemove = (props: MultiValueRemoveProps) => {
  return (
    <components.MultiValueRemove {...props}>
      <MdOutlineClear className="w-3 h-3 text-gray-500" />
    </components.MultiValueRemove>
  )
}

const IndicatorSeparator = (props: IndicatorSeparatorProps) => {
  return <></>
}

const DropdownIndicator = (props: DropdownIndicatorProps) => {
  return (
    <components.DropdownIndicator {...props}>
      <IoChevronDown className="text-gray-900 cursor-pointer" />
    </components.DropdownIndicator>
  )
}

const ClearIndicator = (props: ClearIndicatorProps) => {
  return (
    <components.ClearIndicator {...props}>
      <MdOutlineClear className="cursor-pointer" />
    </components.ClearIndicator>
  )
}

const CutomMenu = ({ children, ...props }: any) => {
  return (
    <components.Menu {...props}>
      <div
        className={`${
          props.isDisableSelect
            ? 'opacity-0.5 pointer-events-none text-gray-400'
            : ''
        }`}
      >
        {children}
      </div>
    </components.Menu>
  )
}

export default function MultiSelect({
  options,
  closeMenuOnSelect = true,
  placeholder = '',
  isDisableSelect = false,
  onSelectValue,
  onFocus,
  onBlur,
  defaultValue,
  ref,
  color = 'grayborder',
  menuPlacement = 'auto',
}: MultiSelectProps) {
  const id = Date.now().toString()
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => setIsMounted(true), [])

  return isMounted ? (
    <Select
      instanceId={id}
      isMulti
      options={options}
      onChange={(e: any) => onSelectValue(e)}
      onFocus={onFocus}
      onBlur={onBlur}
      defaultValue={defaultValue}
      placeholder={placeholder}
      closeMenuOnSelect={closeMenuOnSelect}
      ref={ref}
      menuPlacement={menuPlacement}
      // Rendered into the body rather than in place. Without this the menu is
      // an absolutely-positioned child of the form, so a long option list on
      // one of the last fields (tech stack, expertise areas) is clipped by the
      // form's own box — the list cannot be scrolled to because it never
      // extends the page. Portalling it out escapes every clipping ancestor,
      // and menuPlacement="auto" above then flips it upward when there is no
      // room below. SingleSelect already did this.
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      styles={{
        multiValue: (base) => ({
          ...base,
          borderRadius: 6,
          backgroundColor: '#F3F4F6',
          paddingLeft: 10,
          paddingRight: 10,
        }),
        multiValueLabel: (base) => ({
          ...base,
          fontSize: 12,
          fontWeight: 500,
        }),
        control: (base) => ({
          ...base,
          ...(color === 'failure' ? { 'border-color': 'red' } : {}),
        }),
        // Above modals and the sticky nav, matching SingleSelect.
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      }}
      components={{
        Control: ControlComponent,
        Placeholder,
        Menu: (props) => (
          <CutomMenu {...props} isDisableSelect={isDisableSelect} />
        ),
        IndicatorSeparator,
        DropdownIndicator,
        MultiValueRemove,
        ClearIndicator,
      }}
    />
  ) : (
    <></>
  )
}
